import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { getMicSourceStream, getSourceFromAudioFile } from "./helpers";

import { useAsyncEffect } from "use-async-effect";

interface AnalyzerWorkletParams {
  fftSize: number;
}

interface ChunkDispenserWorkerParams {
  dispensePeriodMs: number;
  chunkDurationMs: number;
  outputSampleRate: number;
}

type ChunkDispenserData = {
  samples: Float32Array;
  startTimestamp: number;
  endTimestamp: number;
};
export type ChunkDispenserCallback = (data: ChunkDispenserData) => void;

type AnalyzerCallbackData = {
  chunk: Float32Array;
  timestamp: number;
};
export type AnalyzerCallback = (data: AnalyzerCallbackData) => void;

interface AudioPipelineProps {
  audioSourceId: string;
  analyzerParams: AnalyzerWorkletParams;
  chunkDispenserParams: ChunkDispenserWorkerParams;
  analyzerCallback: AnalyzerCallback;
  chunkDispenserCallback: ChunkDispenserCallback;
  useMicrophone: boolean;
  useFile?: boolean;
  file?: File | null;
  connectToSpeakers: boolean;
  channelStrategy: "merge" | number;
  gain: number;
}

export const useAudioPipeline = (props: AudioPipelineProps) => {
  const {
    audioSourceId,
    analyzerParams,
    analyzerCallback,
    chunkDispenserParams,
    chunkDispenserCallback,
    useMicrophone,
    useFile = false,
    file,
    connectToSpeakers = false,
    channelStrategy = "merge",
    gain = 1,
  } = props;

  const audioCtxRef = useRef<AudioContext | null>(null);

  const [audioCtxReady, setAudioCtxReady] = useState(false);

  const allNodesRef = useRef<AudioNode[]>([]);

  // Like use memo
  const [_props, _setProps] = useState({});
  useEffect(() => {
    if (!_.isEqual(_props, props)) {
      _setProps(props);
    }
  }, [props]);

  const openAudioContext = async () => {
    setAudioCtxReady(false);
    console.log("Initializing audio context...");
    // Instantiate the audio context
    const ctx = new AudioContext({});
    audioCtxRef.current = ctx;

    // Load the Chunk Dispenser and Custom Analyzer worklets code
    try {
      await ctx.audioWorklet.addModule(`CHUNK_DISPENSER_WORKLET_URL`);
    } catch (error) {
      console.error("Error while loading the Chunk Dispenser worklet:", error);
      closeAudioContext();
      return;
    }
    try {
      await ctx.audioWorklet.addModule(`AUDIO_ANALYZER_WORKLET_URL`);
    } catch (error) {
      console.error("Error while loading the Custom Analyzer worklet:", error);
      return;
    }
    setAudioCtxReady(true);
    console.log("Initialized !");
  };

  const closeAudioContext = () => {
    console.log("Closing Audio Context ...");
    if (audioCtxRef.current) {
      disconnect();
      audioCtxRef.current.close();
      audioCtxRef.current = null;
      console.log("Closed !");
    } else {
      console.log("Nothing to close !");
    }
  };

  useEffect(() => {
    openAudioContext();
    return closeAudioContext;
  }, []);

  // Tricky function, since it needs to be async to get the audio sources
  // This can cause the props to update while this function is still doing it's thing
  // Using useAsyncEffect to mitigate this. It takes the isMounted param to check
  // if this async func should still run or just exit
  const connect = async (isMounted: () => boolean) => {
    console.log("Connecting...");
    if (!audioCtxRef.current) {
      console.error("Called connect while there is no Audio Context");
      return;
    }
    let _allNodes: AudioNode[] = [];

    try {
      let source: AudioNode | null = null;
      if (useFile && file) {
        source = await getSourceFromAudioFile(audioCtxRef.current, file);
      } else if (useMicrophone) {
        source = await getMicSourceStream(audioCtxRef.current, audioSourceId);
      }

      if (!isMounted()) {
        source?.disconnect();
        source = null;
        console.log("Connection aborted !");
        return;
      }

      let lastNode: AudioNode | null = null;
      if (source != null) {
        _allNodes.push(source);
        const gainNode = audioCtxRef.current.createGain();
        gainNode.gain.value = gain;
        lastNode = source.connect(gainNode);
        _allNodes.push(gainNode);

        const chunkDispenser = new AudioWorkletNode(
          audioCtxRef.current,
          "chunk-dispenser-processor",
          {
            processorOptions: {
              inputSampleRate: audioCtxRef.current.sampleRate,
              ...chunkDispenserParams,
            },
          }
        );
        chunkDispenser.port.onmessage = (event) =>
          chunkDispenserCallback(event.data);

        const customAnalyzer = new AudioWorkletNode(
          audioCtxRef.current,
          "custom-analyzer-processor",
          {
            processorOptions: analyzerParams,
          }
        );
        customAnalyzer.port.onmessage = (event) => analyzerCallback(event.data);

        if (channelStrategy === "merge") {
          const merger = audioCtxRef.current.createChannelMerger();
          lastNode = lastNode
            .connect(merger)
            .connect(chunkDispenser)
            .connect(customAnalyzer);
          _allNodes.push(merger, chunkDispenser, customAnalyzer);
        } else {
          const splitter = audioCtxRef.current.createChannelSplitter(
            source.channelCount
          );
          // Select only a specific channel from the input
          lastNode = lastNode
            .connect(splitter)
            .connect(chunkDispenser, channelStrategy, 0)
            .connect(customAnalyzer);
          _allNodes.push(splitter, chunkDispenser, customAnalyzer);
        }

        if (connectToSpeakers) {
          const speakerSplitter = audioCtxRef.current.createChannelSplitter();
          lastNode
            .connect(speakerSplitter)
            .connect(audioCtxRef.current.destination);
          _allNodes.push(speakerSplitter);
        }

        if (useFile) {
          (source as AudioBufferSourceNode).loop = true;
          (source as AudioBufferSourceNode).start();
        }
        allNodesRef.current = _allNodes;

        console.log("Connected !");
      } else {
        console.error("No audio source available");
      }
    } catch (error) {
      console.error("The following gUM error occurred: ", error);
      console.log("Disconnecting the initialized nodes");
      _allNodes.reverse().forEach((node) => {
        node.disconnect();
      });
      allNodesRef.current = [];
    }
  };
  const disconnect = () => {
    console.log("Disconnecting all nodes...");
    allNodesRef.current.reverse().forEach((node) => {
      try {
        console.log("Disconnecting Node: ", { node });
        if (node instanceof AudioWorkletNode) {
          node.port.postMessage({ release: true });
          node.port.onmessage = null;
          node.port.close();
        }
        node.disconnect();
      } catch (err) {
        console.error(err);
      }
    });
    allNodesRef.current = [];
    console.log("Disconnected all nodes.");
  };

  useAsyncEffect(
    (isMounted) => {
      if (audioCtxReady) {
        connect(isMounted);
      }
    },
    disconnect,
    [_props, audioCtxReady]
  );

  return {
    sourceSampleRate: audioCtxRef.current?.sampleRate,
  };
};

export default useAudioPipeline;
